"use client"

import { useEffect, useState } from "react"

export const CHAT_AVOID_SELECTOR = "#analysis-card, [data-chat-avoid]"

export function intersectsViewport(element: Element): boolean {
  const style = window.getComputedStyle(element)
  if (style.display === "none" || style.visibility === "hidden") return false

  const rect = element.getBoundingClientRect()
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  )
}

/**
 * Hides the mobile chat launcher while a conversion-critical surface is in
 * view. Targets can be present at mount (`#analysis-card`) or appear after a
 * client-side search/filter (`data-chat-avoid`), so the observer also watches
 * for DOM additions.
 */
export function useChatAvoidance(enabled: boolean): boolean {
  const [shouldAvoid, setShouldAvoid] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setShouldAvoid(false)
      return
    }

    const evaluate = () => {
      const targets = Array.from(document.querySelectorAll(CHAT_AVOID_SELECTOR))
      setShouldAvoid(targets.some(intersectsViewport))
    }

    const mutationObserver = new MutationObserver(evaluate)
    mutationObserver.observe(document.body, { childList: true, subtree: true })
    window.addEventListener("scroll", evaluate, { passive: true })
    window.addEventListener("resize", evaluate)
    evaluate()

    return () => {
      mutationObserver.disconnect()
      window.removeEventListener("scroll", evaluate)
      window.removeEventListener("resize", evaluate)
    }
  }, [enabled])

  return shouldAvoid
}
